import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { formatDate, formatDateTime } from '../lib/format'
import type { Customer, Nis2Abschnitt, Nis2Bericht } from '../lib/types'
import { ErrorBox, Loading } from './Feedback'

const STATUS: Record<Nis2Abschnitt['status'], { text: string; klasse: string }> = {
  erfuellt: { text: 'erfüllt', klasse: 'badge badge-ok' },
  hinweis: { text: 'Hinweis', klasse: 'badge badge-warn' },
  offen: { text: 'offen', klasse: 'badge badge-crit' },
}

/** „lit. i und j“ – die Fundstelle in Art. 21 Abs. 2. */
export function fundstelle(massnahmen: string[]): string {
  if (massnahmen.length === 0) return ''
  if (massnahmen.length === 1) return `lit. ${massnahmen[0]}`
  return `lit. ${massnahmen.slice(0, -1).join(', ')} und ${massnahmen[massnahmen.length - 1]}`
}

/** Der letzte eingeschlossene Tag eines Zeitraums, dessen Ende ausschließlich ist. */
export function letzterTag(bis: string): string {
  const d = new Date(bis)
  if (Number.isNaN(d.getTime())) return bis
  return new Date(d.getTime() - 1000).toISOString()
}

const DATUM = /^\d{4}-\d{2}-\d{2}$/

/**
 * NIS2-Nachweis (Stufe 5.3): ordnet die Daten der Netzwarte den Maßnahmen
 * aus Art. 21 Abs. 2 der Richtlinie (EU) 2022/2555 zu. Gedruckt oder als PDF
 * gespeichert ist er ein Beleg für technische Maßnahmen an den überwachten
 * Netzwerkgeräten, keine Bewertung der NIS2-Konformität.
 */
export function Nis2Nachweis({ customer }: { customer: Customer }) {
  const [von, setVon] = useState('')
  const [bis, setBis] = useState('')
  const gueltig = (von === '' || DATUM.test(von)) && (bis === '' || DATUM.test(bis))
  const parameter = new URLSearchParams()
  if (von) parameter.set('von', von)
  if (bis) parameter.set('bis', bis)
  const suche = parameter.toString()

  const bericht = useQuery({
    queryKey: ['nis2', customer.id, von, bis],
    queryFn: ({ signal }) => apiGet<Nis2Bericht>(`/customers/${customer.id}/nis2${suche ? `?${suche}` : ''}`, signal),
    enabled: gueltig,
  })

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">NIS2-Nachweis</h1>
          <div className="page-sub">
            {customer.name}
            {bericht.data
              ? ` · ${formatDate(bericht.data.von)} bis ${formatDate(letzterTag(bericht.data.bis))} · erstellt ${formatDateTime(bericht.data.erstellt)}`
              : ''}
          </div>
        </div>
        <div className="row no-print" style={{ gap: 8, alignItems: 'flex-end' }}>
          <div className="field">
            <label htmlFor="nis2-von">Von</label>
            <input id="nis2-von" className="input" type="date" value={von} onChange={(e) => setVon(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="nis2-bis">Bis</label>
            <input id="nis2-bis" className="input" type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
          </div>
          <button type="button" className="btn" onClick={() => window.print()}>
            Drucken
          </button>
        </div>
      </div>

      {bericht.isError ? (
        <ErrorBox error={bericht.error} what="Der NIS2-Nachweis" />
      ) : bericht.isPending ? (
        <Loading what="Den NIS2-Nachweis" />
      ) : Array.isArray(bericht.data.abschnitte) ? (
        <Inhalt r={bericht.data} />
      ) : (
        <ErrorBox error={new Error('Die Zentrale kennt den NIS2-Nachweis noch nicht.')} what="Der NIS2-Nachweis" />
      )}
    </>
  )
}

function Inhalt({ r }: { r: Nis2Bericht }) {
  return (
    <>
      <section className="card">
        <div className="card-body">
          <p style={{ marginTop: 0 }}>
            Der Bericht ordnet die technischen Maßnahmen an den überwachten Netzwerkgeräten den Risikomanagement-
            maßnahmen nach Art. 21 Abs. 2 der Richtlinie (EU) 2022/2555 (NIS2) zu. Er ist ein Beleg, keine Bewertung der
            Konformität. Organisatorische Maßnahmen und die Meldepflichten nach Art. 23 sind nicht erfasst.
          </p>
          <table className="data">
            <thead>
              <tr>
                <th>Art. 21 Abs. 2</th>
                <th>Bereich</th>
                <th>Stand</th>
              </tr>
            </thead>
            <tbody>
              {r.abschnitte.map((a) => (
                <tr key={a.schluessel}>
                  <td>{fundstelle(a.massnahmen)}</td>
                  <td>{a.titel}</td>
                  <td>
                    <span className={STATUS[a.status].klasse}>{STATUS[a.status].text}</span>
                  </td>
                </tr>
              ))}
              {r.nicht_erfasst.map((m) => (
                <tr key={m.buchstabe}>
                  <td>lit. {m.buchstabe}</td>
                  <td>{m.titel}</td>
                  <td>
                    <span className="badge badge-off">organisatorisch, nicht erfasst</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {r.abschnitte.map((a) => (
        <section key={a.schluessel} className="card" aria-label={a.titel}>
          <div className="card-head">
            <h2 className="card-title">{a.titel}</h2>
            <span className="row" style={{ gap: 8 }}>
              <span className="muted">Art. 21 Abs. 2 {fundstelle(a.massnahmen)}</span>
              <span className={STATUS[a.status].klasse}>{STATUS[a.status].text}</span>
            </span>
          </div>
          <div className="card-body">
            {a.kennzahlen.length > 0 ? (
              <table className="data">
                <tbody>
                  {a.kennzahlen.map((k) => (
                    <tr key={k.name}>
                      <td>{k.name}</td>
                      <td style={{ textAlign: 'right' }}>{k.wert}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {a.befunde.length > 0 ? (
              <ul style={{ marginBottom: 0 }}>
                {a.befunde.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ))}

      {r.schwachstellen.length > 0 ? (
        <section className="card">
          <div className="card-head">
            <h2 className="card-title">Anhang: Geräte mit aktiv ausgenutzter Schwachstelle</h2>
          </div>
          <table className="data">
            <thead>
              <tr>
                <th>Gerät</th>
                <th>Firmware</th>
                <th>CVE</th>
              </tr>
            </thead>
            <tbody>
              {r.schwachstellen.map((s) => (
                <tr key={s.geraet}>
                  <td>{s.geraet}</td>
                  <td>{s.firmware}</td>
                  <td>{s.cves.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Anhang: Inventar</h2>
          <span className="muted">{r.geraete.length} Geräte</span>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>Gerät</th>
              <th>Standort</th>
              <th>Hersteller / Modell</th>
              <th>Firmware</th>
              <th>Seriennummer</th>
              <th>Nächstes Ende</th>
            </tr>
          </thead>
          <tbody>
            {r.geraete.map((g) => (
              <tr key={g.id}>
                <td>{g.name}</td>
                <td>{g.standort}</td>
                <td>{[g.hersteller, g.modell].filter(Boolean).join(' ') || '–'}</td>
                <td>{g.firmware || '–'}</td>
                <td>{g.seriennummer || '–'}</td>
                <td>{g.naechstes_ende ? formatDate(g.naechstes_ende) : 'keine Daten'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Anhang: Konfigurationssicherungen im Zeitraum</h2>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>Gerät</th>
              <th>Erfolgreich</th>
              <th>Fehlgeschlagen</th>
              <th>Änderungen</th>
              <th>Letzte erfolgreiche</th>
            </tr>
          </thead>
          <tbody>
            {r.sicherungen.map((s) => (
              <tr key={s.device_id}>
                <td>{s.geraet}</td>
                <td>{s.erfolgreich}</td>
                <td>{s.fehlgeschlagen}</td>
                <td>{s.aenderungen}</td>
                <td>{s.letzte_erfolgreiche ? formatDateTime(s.letzte_erfolgreiche) : 'keine'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Anhang: Konten mit Zugriff</h2>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>Name</th>
              <th>Rollen</th>
              <th>Anmeldung</th>
              <th>Zuletzt angemeldet</th>
            </tr>
          </thead>
          <tbody>
            {r.zugaenge.map((z) => (
              <tr key={z.name}>
                <td>{z.name}</td>
                <td>{z.rollen.join(', ')}</td>
                <td>{z.mfa ? 'Kennwort und TOTP' : z.anmeldedienst ? 'Entra ID' : 'MFA nicht eingerichtet'}</td>
                <td>{z.letzte_anmeldung ? formatDateTime(z.letzte_anmeldung) : '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  )
}
